/**
 * Demo页面主逻辑
 */

import { recommendPallets } from './algorithms/palletRecommendation';
import { mergePallets, calculateMergeEfficiency } from './algorithms/palletMerge';
import { recommendPalletDynamically, initDatabase, getCurrentPalletUsages } from './algorithms/dynamicRecommendation';
import { Mark, AlgorithmConfig, Pallet, SortingDetail } from './types';
import JsBarcode from 'jsbarcode';
import { initDailySorting } from './dailySorting';
import { initTripScanning } from './tripScanning';
import { fetchProcessTimeline, mountProcessPlayer, ProcessPlayerHandle } from './animation';
import { copyText } from './utils/clipboard';

// 当前 dev 实例对应的后端环境：按端口判断（5173=daily / 5174=pre，见 vite.config.ts）
const API_ENV = window.location.port === '5174' ? 'PRE' : 'DAILY';

// 全局变量
let currentPallets: Pallet[] = [];
let currentDetails: SortingDetail[] = [];
let scanHistory: any[] = [];
let mergeInfo: { originalPallets?: number; totalPallets?: number } = {};
let processPlayerHandle: ProcessPlayerHandle | null = null;

// 初始化页面
function initPage() {
    // 标注当前 dev 实例对应的后端环境（DAILY / PRE）
    const envBadge = document.getElementById('envBadge');
    if (envBadge) {
        envBadge.textContent = API_ENV;
        envBadge.classList.toggle('env-badge-pre', API_ENV === 'PRE');
    }

    // 绑定事件监听器
    bindEventListeners();
    
    // 初始化表格
    addEmptyRow();
    
    // 启动时默认加载示例数据
    loadSampleData();
    
    // 初始化日常环境分拣功能
    initDailySorting();

    // 初始化核验/装车扫码功能
    initTripScanning();
}

// 绑定事件监听器
function bindEventListeners() {
    // 数据管理按钮
    document.getElementById('loadSampleData')?.addEventListener('click', loadSampleData);
    document.getElementById('clearData')?.addEventListener('click', clearData);
    document.getElementById('addRow')?.addEventListener('click', addEmptyRow);
    
    // 算法执行按钮
    document.getElementById('runRecommendation')?.addEventListener('click', runRecommendationAlgorithm);
    document.getElementById('runMerge')?.addEventListener('click', runMergeAlgorithm);
    document.getElementById('resetAlgorithms')?.addEventListener('click', resetAlgorithms);
    document.getElementById('playProcess')?.addEventListener('click', openProcessAnimation);
    
    // 模拟扫描按钮
    document.getElementById('simulateScan')?.addEventListener('click', simulateScan);
    
    // 导出测试用例按钮
    document.getElementById('exportTestCase')?.addEventListener('click', exportTestCase);
    
    // 说明按钮
    document.getElementById('showDescription')?.addEventListener('click', showDescriptionModal);
    
    // 模态框控制按钮
    document.getElementById('closeModal')?.addEventListener('click', closeModal);
    document.getElementById('copyTestCase')?.addEventListener('click', copyTestCase);
    document.getElementById('downloadTestCase')?.addEventListener('click', downloadTestCase);
    
    // 说明模态框控制按钮
    document.getElementById('closeDescriptionModal')?.addEventListener('click', closeDescriptionModal);
    document.getElementById('closeDescriptionBtn')?.addEventListener('click', closeDescriptionModal);

    // 分拣过程动画模态框控制按钮
    document.getElementById('closeProcessAnimation')?.addEventListener('click', closeProcessAnimation);
    
    // 侧边栏抽屉控制
    const toggleSidebar = document.getElementById('toggleSidebar');
    const sidebar = document.getElementById('sidebar');
    const sidebarOverlay = document.getElementById('sidebarOverlay');
    
    toggleSidebar?.addEventListener('click', () => {
        sidebar?.classList.toggle('open');
        sidebarOverlay?.classList.toggle('open');
    });
    
    sidebarOverlay?.addEventListener('click', () => {
        sidebar?.classList.remove('open');
        sidebarOverlay?.classList.remove('open');
    });
    
    // 点击模态框外部关闭模态框
    const modal = document.getElementById('testCaseModal');
    modal?.addEventListener('click', (e) => {
        if (e.target === modal) {
            closeModal();
        }
    });
    
    // 点击说明模态框外部关闭模态框
    const descriptionModal = document.getElementById('descriptionModal');
    descriptionModal?.addEventListener('click', (e) => {
        if (e.target === descriptionModal) {
            closeDescriptionModal();
        }
    });

    // 点击过程动画模态框外部关闭模态框
    const processModal = document.getElementById('processAnimationModal');
    processModal?.addEventListener('click', (e) => {
        if (e.target === processModal) {
            closeProcessAnimation();
        }
    });
}

// 加载示例数据
function loadSampleData() {
    const sampleData: Mark[] = [
        { markPrefix: 'WY100001', sku: 'SKU-A', volumePerUnit: 0.2, weightPerUnit: 20, quantity: 2 },
        { markPrefix: 'WY100002', sku: 'SKU-A', volumePerUnit: 0.1, weightPerUnit: 50, quantity: 2 },
        { markPrefix: 'WY100003', sku: 'SKU-B', volumePerUnit: 0.3, weightPerUnit: 100, quantity: 1 },
        { markPrefix: 'WY100004', sku: 'SKU-B', volumePerUnit: 0.05, weightPerUnit: 50, quantity: 4 },
        { markPrefix: 'AABBCC01', sku: 'SKU-C', volumePerUnit: 0.07, weightPerUnit: 80, quantity: 15 },
        { markPrefix: 'AABBCC02', sku: 'SKU-C', volumePerUnit: 0.03, weightPerUnit: 30, quantity: 100 }
    ];
    
    // 清空表格
    clearData();
    
    // 添加示例数据
    sampleData.forEach(data => {
        addRow(data);
    });
}

// 清空数据
function clearData() {
    const tbody = document.querySelector('#dataTable tbody');
    if (tbody) {
        tbody.innerHTML = '';
    }
}

// 添加空行
function addEmptyRow() {
    addRow({ markPrefix: '', sku: '', volumePerUnit: 0, weightPerUnit: 0, quantity: 0 });
}

// 添加数据行
function addRow(data: Mark) {
    const tbody = document.querySelector('#dataTable tbody');
    if (!tbody) return;
    
    const row = document.createElement('tr');
    row.innerHTML = `
        <td><input type="text" class="markPrefix" value="${data.markPrefix}"></td>
        <td><input type="text" class="sku" value="${data.sku ?? ''}" placeholder="SKU"></td>
        <td><input type="number" class="volumePerUnit" value="${data.volumePerUnit}" step="0.01" min="0"></td>
        <td><input type="number" class="weightPerUnit" value="${data.weightPerUnit}" step="1" min="0"></td>
        <td><input type="number" class="quantity" value="${data.quantity}" step="1" min="0"></td>
        <td>
            <div class="row-actions">
                <button class="copy-row">复制</button>
                <button class="remove-row">删除</button>
            </div>
        </td>
    `;
    
    // 添加删除按钮事件
    const removeButton = row.querySelector('.remove-row');
    removeButton?.addEventListener('click', () => {
        row.remove();
    });
    
    // 添加复制按钮事件
    const copyButton = row.querySelector('.copy-row');
    copyButton?.addEventListener('click', () => {
        const markPrefix = (row.querySelector('.markPrefix') as HTMLInputElement).value;
        const sku = (row.querySelector('.sku') as HTMLInputElement).value;
        const volumePerUnit = parseFloat((row.querySelector('.volumePerUnit') as HTMLInputElement).value) || 0;
        const weightPerUnit = parseFloat((row.querySelector('.weightPerUnit') as HTMLInputElement).value) || 0;
        const quantity = parseInt((row.querySelector('.quantity') as HTMLInputElement).value) || 0;

        // 添加新行复制当前数据
        addRow({ markPrefix, sku, volumePerUnit, weightPerUnit, quantity });
    });
    
    tbody.appendChild(row);
}

// 获取表格数据
function getTableData(): Mark[] {
    const rows = document.querySelectorAll('#dataTable tbody tr');
    const data: Mark[] = [];
    
    rows.forEach(row => {
        const markPrefix = (row.querySelector('.markPrefix') as HTMLInputElement).value;
        const sku = (row.querySelector('.sku') as HTMLInputElement).value;
        const volumePerUnit = parseFloat((row.querySelector('.volumePerUnit') as HTMLInputElement).value) || 0;
        const weightPerUnit = parseFloat((row.querySelector('.weightPerUnit') as HTMLInputElement).value) || 0;
        const quantity = parseInt((row.querySelector('.quantity') as HTMLInputElement).value) || 0;

        // 跳过空行（以唛头前缀 + SKU 为必填）
        if (markPrefix && quantity > 0) {
            data.push({ markPrefix, sku, volumePerUnit, weightPerUnit, quantity });
        }
    });
    
    return data;
}

// 获取算法配置
function getAlgorithmConfig(): AlgorithmConfig {
    return {
        palletVolumeLimit: parseFloat((document.getElementById('palletVolumeLimit') as HTMLInputElement).value) || 1.8,
        palletWeightLimit: parseInt((document.getElementById('palletWeightLimit') as HTMLInputElement).value) || 1000
    };
}

// 执行托盘推荐算法
function runRecommendationAlgorithm() {
    const data = getTableData();
    if (data.length === 0) {
        alert('请先输入数据');
        return;
    }
    
    const config = getAlgorithmConfig();
    const result = recommendPallets(data, config);
    
    currentPallets = result.pallets;
    currentDetails = result.pallets.flatMap(pallet => pallet.details);
    
    // 初始化动态推荐算法的数据库，传入托盘信息用于获取预计件数
    initDatabase(currentDetails, currentPallets);
    
    // 显示结果
    displayAlgorithmResults(result);
    
    // 更新实时托盘状态
    updateRealtimePallets();
}

// 执行托盘合并算法
function runMergeAlgorithm() {
    if (currentPallets.length === 0) {
        alert('请先执行托盘推荐算法');
        return;
    }
    
    const config = getAlgorithmConfig();
    const result = mergePallets(currentPallets, config);
    
    // 存储合并信息
    mergeInfo = {
        originalPallets: result.originalPallets,
        totalPallets: result.totalPallets
    };
    
    // 更新当前托盘为合并后的结果
    currentPallets = result.pallets;
    currentDetails = result.pallets.flatMap(pallet => pallet.details);
    
    // 重新初始化动态推荐算法的数据库，使用合并后的数据，传入托盘信息用于获取预计件数
    initDatabase(currentDetails, currentPallets);
    
    // 显示结果
    displayAlgorithmResults(result);
    
    // 更新实时托盘状态
    updateRealtimePallets();
}

// 重置算法
function resetAlgorithms() {
    currentPallets = [];
    currentDetails = [];

    // 清空结果
    document.getElementById('algorithmResults')!.innerHTML = `
        <div class="empty-state">
            <svg width="48" height="48" viewBox="0 0 20 20" fill="none" opacity="0.3">
                <path d="M9 2H3C2.44772 2 2 2.44772 2 3V17C2 17.5523 2.44772 18 3 18H17C17.5523 18 18 17.5523 18 17V11" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                <path d="M13 2L18 7" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                <path d="M12 8L18 2" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
            </svg>
            <p>执行算法后查看结果</p>
        </div>
    `;
    document.getElementById('scanResults')!.innerHTML = '';
    document.getElementById('realtimePallets')!.innerHTML = `
        <div class="empty-state">
            <svg width="48" height="48" viewBox="0 0 20 20" fill="none" opacity="0.3">
                <circle cx="10" cy="10" r="7" stroke="currentColor" stroke-width="2"/>
                <path d="M10 6V10L13 13" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            <p>暂无托盘数据</p>
        </div>
    `;
}

// 打开分拣过程动画
async function openProcessAnimation() {
    const data = getTableData();
    if (data.length === 0) {
        alert('请先输入唛头数据');
        return;
    }

    const mount = document.getElementById('processAnimationMount');
    const modal = document.getElementById('processAnimationModal');
    if (!mount || !modal) return;

    // 复用已有 handle，避免重复挂载
    processPlayerHandle?.dispose();
    mount.innerHTML = '';

    // fetchProcessTimeline 返回 FrameProvider：真实后端就绪则走重建，否则回退 mock ProcessFrame[]（仍走重建）
    const provider = await fetchProcessTimeline({ marks: data, config: getAlgorithmConfig() });
    processPlayerHandle = mountProcessPlayer(mount, provider);

    modal.classList.add('active');
}

// 关闭分拣过程动画
function closeProcessAnimation() {
    const modal = document.getElementById('processAnimationModal');
    modal?.classList.remove('active');
    processPlayerHandle?.dispose();
    processPlayerHandle = null;
}

// 初始化条形码
function initBarcodes() {
    // 为每个扫描按钮容器添加事件监听器
    const containers = document.querySelectorAll('.scan-button-container');
    containers.forEach(container => {
        const button = container.querySelector('button');
        const tooltip = container.querySelector('.barcode-tooltip');
        const barcodeContainer = container.querySelector('.barcode-container');
        
        if (button && tooltip && barcodeContainer) {
            // 生成条形码
            const mark = barcodeContainer.getAttribute('data-mark');
            if (mark) {
                // 创建canvas元素用于生成条形码
                const canvas = document.createElement('canvas');
                canvas.width = 180;
                canvas.height = 80;
                barcodeContainer.appendChild(canvas);
                
                // 使用jsbarcode生成条形码
                JsBarcode(canvas, mark, {
                    format: 'CODE128',
                    width: 2,
                    height: 60,
                    displayValue: false,
                    margin: 10
                });
            }
        }
    });
}

// 显示算法结果
function displayAlgorithmResults(result: any) {
    const resultsContainer = document.getElementById('algorithmResults');
    if (!resultsContainer) return;
    
    let html = '';
    
    // 显示总托盘数
    html += `<div class="result-summary">
        <div class="summary-item">
            <span class="summary-label">总托盘数</span>
            <span class="summary-value">${result.totalPallets}</span>
        </div>
    `;
    
    // 如果是合并结果，显示合并效率
    if (result.originalPallets) {
        const efficiency = calculateMergeEfficiency(result.originalPallets, result.totalPallets);
        html += `
        <div class="summary-item">
            <span class="summary-label">合并前</span>
            <span class="summary-value">${result.originalPallets}</span>
        </div>
        <div class="summary-item">
            <span class="summary-label">合并效率</span>
            <span class="summary-value">${efficiency}%</span>
        </div>
    `;
    }
    
    html += '</div>';
    
    // 显示每个托盘的详情
    result.pallets.forEach((pallet: Pallet) => {
        html += `<div class="pallet-card">
            <div class="pallet-header">
                <span class="pallet-title">${pallet.palletNo}</span>
                <span class="pallet-customer">${pallet.sku ?? pallet.customer}</span>
            </div>
            <div class="pallet-stats">
                <div class="stat-item">
                    <div class="stat-value">${pallet.totalQuantity}</div>
                    <div class="stat-label">总件数</div>
                </div>
                <div class="stat-item">
                    <div class="stat-value">${pallet.totalVolume.toFixed(2)}</div>
                    <div class="stat-label">总体积(CBM)</div>
                </div>
                <div class="stat-item">
                    <div class="stat-value">${pallet.totalWeight.toFixed(2)}</div>
                    <div class="stat-label">总重量(KGS)</div>
                </div>
            </div>
            <div class="pallet-details-title">货物明细</div>
            <ul class="pallet-details-list">
        `;
        
        // 按唛头分组显示明细
        const detailsByMark = new Map<string, SortingDetail[]>();
        pallet.details.forEach(detail => {
            if (!detailsByMark.has(detail.markPrefix)) {
                detailsByMark.set(detail.markPrefix, []);
            }
            detailsByMark.get(detail.markPrefix)!.push(detail);
        });
        
        detailsByMark.forEach((details, markPrefix) => {
            html += `<li class="detail-item">
                <div class="detail-info">
                    <span class="detail-mark">${markPrefix}</span>
                    <span class="detail-count">(${details.length}件)</span>
                </div>
                <div class="detail-actions">`;
            details.forEach((detail) => {
                const buttonClass = detail.status === 'completed' ? 'scan-button completed' : 'scan-button';
                const fullMark = `${detail.markPrefix}/${detail.markSerialNo}`;
                html += `<div class="scan-button-container">
                    <button class="${buttonClass}" onclick="simulateDetailScan('${detail.markPrefix}', '${detail.markSerialNo}')">扫描 ${detail.markSerialNo}</button>
                    <div class="barcode-tooltip">
                        <div class="barcode-container" data-mark="${fullMark}"></div>
                        <div class="barcode-label">${fullMark}</div>
                    </div>
                </div>`;
            });
            html += `</div>
            </li>`;
        });
        
        html += `
            </ul>
        </div>`;
    });
    
    resultsContainer.innerHTML = html;
    
    // 初始化条形码
    initBarcodes();
}

// 模拟扫描单个明细
function simulateDetailScan(markPrefix: string, markSerialNo: string) {
    const config = getAlgorithmConfig();
    const result = recommendPalletDynamically(markPrefix, markSerialNo, config);
    
    // 如果扫描成功，更新明细状态为已扫描
    if (result.success) {
        // 查找并更新对应明细的状态
        currentDetails.forEach(detail => {
            if (detail.markPrefix === markPrefix && detail.markSerialNo === markSerialNo) {
                detail.status = 'completed';
            }
        });
        
        // 同步更新托盘中的明细状态
        currentPallets.forEach(pallet => {
            pallet.details.forEach(detail => {
                if (detail.markPrefix === markPrefix && detail.markSerialNo === markSerialNo) {
                    detail.status = 'completed';
                }
            });
        });
        
        // 更新数据库中的明细状态
        updateDetailStatus(markPrefix, markSerialNo, 'completed');
        
        // 刷新托盘显示以更新按钮样式
        refreshPalletDisplay();
    }
    
    displayScanResult(result);
    updateRealtimePallets();
    
    // 记录扫描过程
    const mark = `${markPrefix}/${markSerialNo}`;
    const scanInfo = {
        mark: mark,
        markPrefix: markPrefix,
        serialNo: markSerialNo,
        success: result.success,
        message: result.success ? result.message : result.error,
        palletNo: result.palletNo,
        palletInfo: result.palletInfo,
        timestamp: new Date().toISOString(),
        palletStatus: getCurrentPalletUsages()
    };
    scanHistory.push(scanInfo);
}

// 更新明细状态
function updateDetailStatus(markPrefix: string, markSerialNo: string, status: 'pending' | 'completed') {
    // 更新 currentDetails
    const detail = currentDetails.find(d => 
        d.markPrefix === markPrefix && d.markSerialNo === markSerialNo
    );
    if (detail) {
        detail.status = status;
    }
}

// 刷新托盘显示
function refreshPalletDisplay() {
    // 重新显示当前托盘结果以更新按钮样式
    if (currentPallets.length > 0) {
        const result = {
            pallets: currentPallets,
            totalPallets: currentPallets.length
        };
        displayAlgorithmResults(result);
    }
}

// 提取唛头中的最后一段连续数字
function extractLastContinuousNumber(input: string): string | null {
    if (!input || input.trim().length === 0) {
        return null;
    }
    
    // 检查是否包含序号分隔符
    if (input.includes('/') || input.includes('-')) {
        // 处理分隔符（/、-替换为空格）
        const processedStr = input.replace(/[/-]/g, ' ').trim();
        
        // 匹配末尾连续数字
        const match = processedStr.match(/\d+$/);
        if (match) {
            return match[0];
        }
    }
    
    // 没有分隔符，不提取序号
    return null;
}

// 从唛头中提取前缀（去除序号部分）
function extractMarkPrefix(input: string): string {
    if (!input || input.trim().length === 0) {
        return input;
    }
    
    // 检查是否包含序号分隔符
    if (input.includes('/') || input.includes('-')) {
        // 处理分隔符（/、-替换为空格）
        const processedStr = input.replace(/[/-]/g, ' ').trim();
        
        // 去除末尾连续数字
        const prefix = processedStr.replace(/\d+$/, '').trim();
        
        // 如果前缀为空，返回原始输入
        return prefix || input;
    } else {
        // 没有分隔符，直接返回原始输入作为前缀
        return input;
    }
}

// 模拟扫描
function simulateScan() {
    const mark = (document.getElementById('mark') as HTMLInputElement).value;
    
    if (!mark) {
        alert('请输入唛头');
        return;
    }
    
    // 提取序号和前缀
    const serialNo = extractLastContinuousNumber(mark);
    const markPrefix = extractMarkPrefix(mark);
    
    const config = getAlgorithmConfig();
    const result = recommendPalletDynamically(markPrefix, serialNo || '', config);
    
    // 如果扫描成功，更新明细状态为已扫描
    if (result.success) {
        // 查找匹配的明细并更新状态
        currentDetails.forEach(detail => {
            if (detail.markPrefix === markPrefix && 
                (serialNo ? detail.markSerialNo === serialNo : true)) {
                detail.status = 'completed';
            }
        });
        
        // 同步更新托盘中的明细状态
        currentPallets.forEach(pallet => {
            pallet.details.forEach(detail => {
                if (detail.markPrefix === markPrefix && 
                    (serialNo ? detail.markSerialNo === serialNo : true)) {
                    detail.status = 'completed';
                }
            });
        });
        
        // 刷新托盘显示以更新按钮样式
        refreshPalletDisplay();
    }
    
    displayScanResult(result);
    updateRealtimePallets();
    
    // 记录扫描过程
    const scanInfo = {
        mark: mark,
        markPrefix: markPrefix,
        serialNo: serialNo || '',
        success: result.success,
        message: result.success ? result.message : result.error,
        palletNo: result.palletNo,
        palletInfo: result.palletInfo,
        timestamp: new Date().toISOString(),
        palletStatus: getCurrentPalletUsages()
    };
    scanHistory.push(scanInfo);
}

// 显示扫描结果
function displayScanResult(result: any) {
    const resultsContainer = document.getElementById('scanResults');
    if (!resultsContainer) return;
    
    let html = '';
    
    if (result.success) {
        html += `<div class="result-message success">
            <div style="font-weight: 600; margin-bottom: 8px;">✓ 扫描成功 | ${result.palletNo} | ${result.message}</div>
        `;
        
        if (result.palletInfo) {
            html += `
            <div style="margin-top: 12px; padding-top: 12px; border-top: 1px solid rgba(34, 197, 94, 0.3); display: grid; grid-template-columns: repeat(2, 1fr); gap: 8px; font-size: 0.8125rem;">
                <div><strong>${result.palletInfo.usedQuantity}</strong>/<strong>${result.palletInfo.estimatedQuantity}</strong></div>
                <div><strong>${result.palletInfo.usedVolume.toFixed(2)}</strong>CBM <strong>${result.palletInfo.usedWeight.toFixed(2)}</strong>KGS</div>
            </div>
            `;
        }
        
        html += '</div>';
    } else {
        html += `<div class="result-message error">
            <div style="font-weight: 600; margin-bottom: 8px;">✗ 扫描失败 | ${result.error}</div>
        `;
        
        if (result.palletNo) {
            html += `<div style="margin-top: 8px; font-family: var(--font-mono); font-size: 0.8125rem;">托盘: ${result.palletNo}</div>`;
        }
        
        if (result.palletInfo) {
            html += `
            <div style="margin-top: 12px; padding-top: 12px; border-top: 1px solid rgba(239, 68, 68, 0.3); display: grid; grid-template-columns: repeat(2, 1fr); gap: 8px; font-size: 0.8125rem;">
                <div><strong>${result.palletInfo.usedQuantity}</strong>/<strong>${result.palletInfo.estimatedQuantity}</strong></div>
                <div><strong>${result.palletInfo.usedVolume.toFixed(2)}</strong>CBM <strong>${result.palletInfo.usedWeight.toFixed(2)}</strong>KGS</div>
            </div>
            `;
        }
        
        html += '</div>';
    }
    
    resultsContainer.innerHTML = html;
}

// 更新实时托盘状态
function updateRealtimePallets() {
    const palletsContainer = document.getElementById('realtimePallets');
    if (!palletsContainer) return;
    
    const palletUsages = getCurrentPalletUsages();
    
    let html = '';
    
    if (palletUsages.length === 0) {
        html = `
        <div class="empty-state">
            <svg width="48" height="48" viewBox="0 0 20 20" fill="none" opacity="0.3">
                <circle cx="10" cy="10" r="7" stroke="currentColor" stroke-width="2"/>
                <path d="M10 6V10L13 13" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            <p>暂无托盘数据</p>
        </div>`;
    } else {
        palletUsages.forEach(pallet => {
            const statusClass = pallet.status === 'in_use' ? 'in_use' : 'completed';
            const statusText = pallet.status === 'in_use' ? '使用中' : '已完成';
            
            html += `<div class="realtime-pallet">
                <div class="realtime-pallet-header">
                    <span class="realtime-pallet-title">${pallet.palletNo}</span>
                    <span class="realtime-pallet-status ${statusClass}">${statusText}</span>
                </div>
                <div class="realtime-pallet-stats">
                    <div class="realtime-stat">
                        <span class="realtime-stat-label">当前重量</span>
                        <span class="realtime-stat-value">${pallet.currentWeight.toFixed(2)} KGS</span>
                    </div>
                    <div class="realtime-stat">
                        <span class="realtime-stat-label">当前体积</span>
                        <span class="realtime-stat-value">${pallet.currentVolume.toFixed(2)} CBM</span>
                    </div>
                    <div class="realtime-stat">
                        <span class="realtime-stat-label">预计件数</span>
                        <span class="realtime-stat-value">${pallet.estimatedQuantity}</span>
                    </div>
                    <div class="realtime-stat">
                        <span class="realtime-stat-label">实际件数</span>
                        <span class="realtime-stat-value">${pallet.actualQuantity}</span>
                    </div>
                </div>
                <div class="loaded-details">
                    <div class="loaded-details-title">已装载货物明细</div>
                    <ul class="loaded-details-list">
            `;
            
            if (pallet.loadedDetails && pallet.loadedDetails.length > 0) {
                pallet.loadedDetails.forEach(detail => {
                    html += `
                        <li class="loaded-detail-item">
                            <span class="loaded-detail-mark">${detail.markPrefix}/${detail.markSerialNo}</span>
                            <span class="loaded-detail-info">${detail.weight} KGS · ${detail.volume} CBM</span>
                        </li>`;
                });
            } else {
                html += '<li class="loaded-detail-item" style="justify-content: center; color: var(--text-tertiary);">暂无货物</li>';
            }
            
            html += `
                    </ul>
                </div>
            </div>`;
        });
    }
    
    palletsContainer.innerHTML = html;
}

// 导出测试用例
function exportTestCase() {
    const data = getTableData();
    if (data.length === 0) {
        alert('请先输入数据');
        return;
    }
    
    if (currentPallets.length === 0) {
        alert('请先执行算法获取结果');
        return;
    }
    
    const testCaseMarkdown = generateTestCaseMarkdown();
    
    // 显示模态框
    const modal = document.getElementById('testCaseModal');
    const preview = document.getElementById('testCasePreview');
    
    if (preview) {
        preview.innerHTML = markdownToHtml(testCaseMarkdown);
    }
    
    if (modal) {
        modal.classList.add('active');
    }
    
    // 存储测试用例内容到全局变量，供复制和下载使用
    (window as any).currentTestCaseMarkdown = testCaseMarkdown;
}

// 关闭模态框
function closeModal() {
    const modal = document.getElementById('testCaseModal');
    if (modal) {
        modal.classList.remove('active');
    }
}

// 显示说明模态框
function showDescriptionModal() {
    const modal = document.getElementById('descriptionModal');
    if (modal) {
        modal.classList.add('active');
    }
}

// 关闭说明模态框
function closeDescriptionModal() {
    const modal = document.getElementById('descriptionModal');
    if (modal) {
        modal.classList.remove('active');
    }
}

// 复制测试用例到剪贴板
function copyTestCase() {
    const testCaseMarkdown = (window as any).currentTestCaseMarkdown;
    if (!testCaseMarkdown) return;
    
    copyText(testCaseMarkdown).then(() => {
        // 找到复制按钮并添加打钩状态
        const copyButton = document.getElementById('copyTestCase') as HTMLButtonElement;
        if (copyButton) {
            // 保存原始内容
            const originalContent = copyButton.innerHTML;
            // 改为打钩状态
            copyButton.innerHTML = `
                <svg width="14" height="14" viewBox="0 0 20 20" fill="none">
                    <path d="M6 11L9 14L14 8" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                已复制
            `;
            // 禁用按钮
            copyButton.disabled = true;
            // 3秒后恢复原始状态
            setTimeout(() => {
                copyButton.innerHTML = originalContent;
                copyButton.disabled = false;
            }, 3000);
        }
    }).catch(err => {
        console.error('复制失败:', err);
        alert('复制失败，请手动复制');
    });
}

// 下载测试用例为markdown文件
function downloadTestCase() {
    const testCaseMarkdown = (window as any).currentTestCaseMarkdown;
    if (!testCaseMarkdown) return;
    
    // 生成TCyyyymmddHH24miss格式的文件名
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    const seconds = String(now.getSeconds()).padStart(2, '0');
    const fileName = `TC${year}${month}${day}${hours}${minutes}${seconds}.md`;
    
    const blob = new Blob([testCaseMarkdown], { type: 'text/markdown;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
}

// 生成测试用例markdown内容
function generateTestCaseMarkdown() {
    const config = getAlgorithmConfig();
    const data = getTableData();
    
    let markdown = '# 测试用例\n';
    
    // 执行参数
    markdown += '## 执行参数\n';
    markdown += '### 分拣配置\n';
    markdown += `1. 托盘体积上限：${config.palletVolumeLimit}\n`;
    markdown += `2. 托盘重量上限: ${config.palletWeightLimit}\n`;
    
    // 数据输入
    markdown += '### 数据输入\n';
    markdown += '| 唛头前缀 | SKU | 单件体积(CBM) | 单件重量(KGS) | 件数|\n';
    markdown += '| -------- | --- | --------- | --------- | ---|\n';

    data.forEach(item => {
        markdown += `| ${item.markPrefix} | ${item.sku ?? ''} | ${item.volumePerUnit} | ${item.weightPerUnit} | ${item.quantity}|\n`;
    });
    
    // 过程&结果
    markdown += '\n## 过程&结果\n';
    markdown += '### 托盘推荐&合并\n';
    markdown += '#### 算法结果\n';
    markdown += `1. 总托盘数：${currentPallets.length}\n`;
    
    // 添加合并信息
    if (mergeInfo.originalPallets) {
        const efficiency = Math.round(((mergeInfo.originalPallets - currentPallets.length) / mergeInfo.originalPallets) * 100);
        markdown += `2. 合并前：${mergeInfo.originalPallets}\n`;
        markdown += `3. 合并效率：${efficiency}%\n`;
    }
    
    // 托盘明细
    markdown += '\n#### 逻辑托盘明细\n';
    currentPallets.forEach((pallet) => {
        markdown += `##### ${pallet.palletNo}\n`;
        markdown += `1. 总件数：${pallet.totalQuantity}\n`;
        markdown += `2. 总体积：${pallet.totalVolume.toFixed(2)}CBM\n`;
        markdown += `3. 总重量：${pallet.totalWeight.toFixed(2)}KGS\n`;
        
        // 货物明细
        const details = pallet.details.map(detail => `${detail.markPrefix}/${detail.markSerialNo}`).join(',');
        markdown += `4. 货物明细：${details}\n`;
    });
    
    // 扫描过程&结果
    markdown += '\n### 扫描过程\u0026结果\n';
    if (scanHistory.length > 0) {
        scanHistory.forEach((scan) => {
            // 查找对应的明细以获取体积和重量
            let volume = 0;
            let weight = 0;
            currentDetails.forEach(detail => {
                if (detail.markPrefix === scan.markPrefix && 
                    (scan.serialNo ? detail.markSerialNo === scan.serialNo : true)) {
                    volume = detail.volume;
                    weight = detail.weight;
                }
            });
            
            markdown += `#### ${scan.mark} ${volume.toFixed(2)}CBM ${weight.toFixed(2)}KGS\n`;
            markdown += `1. ${scan.success ? '扫描成功' : '扫描失败'}\n`;
            markdown += `2. ${scan.message}\n`;
            markdown += `3. ${scan.palletNo}\n`;
            
            if (scan.palletInfo) {
                markdown += `4. ${scan.palletInfo.usedQuantity}/${scan.palletInfo.estimatedQuantity} ${scan.palletInfo.usedVolume.toFixed(2)}CBM ${scan.palletInfo.usedWeight.toFixed(2)}KGS\n`;
            }
        });
    } else {
        markdown += '#### 暂无扫描记录\n';
    }
    
    return markdown;
}

// 将markdown转换为html用于预览
function markdownToHtml(markdown: string) {
    return markdown
        .replace(/^# (.*$)/gim, '<h1>$1</h1>')
        .replace(/^## (.*$)/gim, '<h2>$1</h2>')
        .replace(/^### (.*$)/gim, '<h3>$1</h3>')
        .replace(/^#### (.*$)/gim, '<h4>$1</h4>')
        .replace(/^\*\*\*(.*)\*\*\*$/gim, '<strong><em>$1</em></strong>')
        .replace(/^\*\*(.*)\*\*$/gim, '<strong>$1</strong>')
        .replace(/^\*(.*)$/gim, '<li>$1</li>')
        .replace(/(<li>.*<\/li>)/s, '<ul>$1</ul>')
        .replace(/\|(.*)\|(.*)\|(.*)\|(.*)\|(.*)\|/gim, '<tr><td>$1</td><td>$2</td><td>$3</td><td>$4</td><td>$5</td></tr>')
        .replace(/(<tr>.*<\/tr>)/s, '<table>$1</table>')
        .replace(/\n/gim, '<br>');
}

// 初始化页面
window.onload = initPage;

// 暴露函数给全局作用域
(window as any).simulateDetailScan = simulateDetailScan;
